import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { useScrollContainerRef } from './useScrollContainer';

const SCROLL_KEY_PREFIX = 'lemoment:scroll:';
const MAX_KEYS = 50;

const scrollKey = (locationKey: string) => `${SCROLL_KEY_PREFIX}${locationKey}`;

const recentKeys: string[] = [];

const touchKey = (key: string) => {
  const existing = recentKeys.indexOf(key);
  if (existing !== -1) {
    recentKeys.splice(existing, 1);
  }
  recentKeys.push(key);
  while (recentKeys.length > MAX_KEYS) {
    const evicted = recentKeys.shift();
    if (evicted) {
      try {
        sessionStorage.removeItem(scrollKey(evicted));
      } catch {
        // sessionStorage can throw in private-browsing modes — non-fatal.
      }
    }
  }
};

const writeScroll = (key: string, top: number) => {
  try {
    sessionStorage.setItem(scrollKey(key), String(top));
    touchKey(key);
  } catch {
    // Quota or restricted storage — best-effort.
  }
};

const readScroll = (key: string): number => {
  try {
    const saved = sessionStorage.getItem(scrollKey(key));
    const y = saved ? Number(saved) : 0;
    return Number.isFinite(y) ? y : 0;
  } catch {
    // sessionStorage can throw in private-browsing modes — fall back to top.
    return 0;
  }
};

/**
 * Native-like scroll restoration for BrowserRouter. Saves the app scroll
 * container's `scrollTop` per `location.key` while scrolling and on unload,
 * then restores on POP (back/forward gesture) and resets to top on
 * PUSH/REPLACE.
 *
 * Scrolling lives on `.app-scroll` (see `useScrollContainer`), not the
 * document, so this targets the container element. BrowserRouter cannot use
 * React Router's `<ScrollRestoration />` (data-router only); this reproduces
 * the behavior via `sessionStorage` with an LRU cap to bound growth.
 */
export const useScrollRestoration = (): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const containerRef = useScrollContainerRef();
  const currentKeyRef = useRef(location.key);

  // Restore in a layout effect (pre-paint) so the scroll lands before the
  // browser paints — and, under legacyFlushSync, before the View
  // Transitions new snapshot is captured — so the snapshot already sits at
  // the restored position and there is no visible jump.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    // jsdom (tests) doesn't implement Element.scrollTo — fall back to the
    // scrollTop property so this is inert there instead of throwing.
    const scrollTo = (top: number) => {
      if (typeof el.scrollTo === 'function') {
        el.scrollTo(0, top);
      } else {
        el.scrollTop = top;
      }
    };
    currentKeyRef.current = location.key;
    touchKey(location.key);
    if (navigationType === 'POP') {
      scrollTo(readScroll(location.key));
      return;
    }
    scrollTo(0);
  }, [location.key, navigationType, containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    let frame: number | null = null;
    let lastWritten = -1;
    const onScroll = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const y = el.scrollTop;
        if (y === lastWritten) {
          return;
        }
        lastWritten = y;
        writeScroll(currentKeyRef.current, y);
      });
    };
    const onUnload = () => writeScroll(currentKeyRef.current, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onUnload);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onUnload);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [containerRef]);
};
